import React, {FC, useCallback, useMemo, useState} from 'react';
import {EquipmentRepairRequestEntity} from "../../../../graphql/graphQlApiHooks";
import {DataGrid, GridColumns, GridRowData, GridSortModel} from "@mui/x-data-grid";
import {Box, Menu} from "@mui/material";
import {
    RepairRequestsListPagination
} from "../../../../store/reactiveVarsStores/repair-requests/repairRequestsListPagination";
import {
    RepairRequestsListSortOrder,
    RepairRequestsListSortOrderInitial
} from "../../../../store/reactiveVarsStores/repair-requests/repairRequestsListSortOrder";
import {SortDirectionEnum} from "../../../../store/reactiveVarsStores/spor-equipmnts/sort-direction.enum";
import { SortRepairRequestColumnsEnum } from "../../../../store/reactiveVarsStores/repair-requests/sort-repair-request-columns.enum";
import {
    EquipmentCell,
    SportObjectCell,
    EquipmentTypeCell,
    SportKindCell,
    StatusCell,
    HistoryCell,
    ActionsCell,
    DateCell
} from "./CellRenderers";

interface Props {
    equipmentRepairRequest?: EquipmentRepairRequestEntity[]
    allCount?: number
    loading?: boolean

    pagination: RepairRequestsListPagination;
    sortOrderModel: RepairRequestsListSortOrder;

    actions: {
        setPagination: (pagination: RepairRequestsListPagination) => void;
        setOrder: (orderModel: RepairRequestsListSortOrder) => void;
    }
}


function MoreMenuItems(props: { onClose: () => void, equipmentRepairRequest: EquipmentRepairRequestEntity }) {
    return null;
}

const List: FC<Props> = (props: Props) => {
    const {
        equipmentRepairRequest,
        allCount,
        loading,
        pagination,
        sortOrderModel,
        actions: {
            setPagination,
            setOrder
        }
    } = props;

    const sortModelChangeHandler = useCallback((model: GridSortModel) => {
        const sortItem = model?.[0];
        if (!sortItem || !sortItem.sort) {
            setOrder(RepairRequestsListSortOrderInitial);
            return;
        }

        const direction = sortItem.sort.toUpperCase() as SortDirectionEnum;
        setOrder({ [sortItem.field]: direction } as RepairRequestsListSortOrder);
    }, [setOrder]);

    const [contextRow, setContextRow] = useState<EquipmentRepairRequestEntity | null>(null);
    const [contextMenu, setContextMenu] = useState<{
        mouseX: number;
        mouseY: number;
    } | null>(null);
    
    const contextMenuCloseHandler = useCallback(() => {
        setContextMenu(null);
    }, []);

    const doubleClickHandler = useCallback((id: string) => {
        //TODO вынести в action
        console.log('doubleClickHandler', id);
    }, []);

    const handlePageChange = useCallback((page: number) => {
        setPagination({...pagination, page: page});
    }, [pagination, setPagination]);

    const handlePageSizeChange = useCallback((pageSize: number) => {
        setPagination({...pagination, pageSize: pageSize});
    }, [pagination, setPagination]);

    const columns: GridColumns<EquipmentRepairRequestEntity> = useMemo(() => [
        {
            field: SortRepairRequestColumnsEnum.NAME,
            headerName: "Оборудование",
            renderCell: (params) => <EquipmentCell row={params.row} />,
            minWidth: 300,
            flex: 2.5,
            disableColumnMenu: true,
            editable: false
        },
        {
            field: SortRepairRequestColumnsEnum.CREATED_AT,
            headerName: "Дата подачи",
            renderCell: (params) => <DateCell createdAt={params.row.createdAt} />,
            minWidth: 150,
            flex: 1,
            disableColumnMenu: true,
            editable: false,
            align: "center",
            headerAlign: "center",
        },
        {
            field: SortRepairRequestColumnsEnum.SPORT_OBJECT,
            headerName: "Спортивный объект",
            editable: false,
            align: "center",
            headerAlign: "center",
            disableColumnMenu: true,
            flex: 1,
            renderCell: ({row: {sportEquipment: {sportObject, inUse}}}: { row: EquipmentRepairRequestEntity }) => {
                return (
                    <SportObjectCell sportObject={sportObject} inUse={inUse}/>
                );
            },
        },
        {
            field: SortRepairRequestColumnsEnum.EQUIPMENT_TYPE,
            headerName: "Тип оборудования",
            editable: false,
            align: "center",
            headerAlign: "center",
            disableColumnMenu: true,
            flex: 1,
            renderCell: ({row: {sportEquipment: {equipmentTypeObject, inUse}}}: { row: EquipmentRepairRequestEntity }) => {
                return (
                    <EquipmentTypeCell equipmentTypeObject={equipmentTypeObject} inUse={inUse}/>
                );
            },
        },
        {
            field: SortRepairRequestColumnsEnum.SPORT_KIND,
            headerName: "Вид спорта",
            editable: false,
            align: "center",
            headerAlign: "center",
            disableColumnMenu: true,
            flex: 1,
            renderCell: ({row: {sportEquipment: {sportKindObject, inUse}}}: { row: EquipmentRepairRequestEntity }) => {
                return (
                    <SportKindCell sportKindObject={sportKindObject} inUse={inUse}/>
                );
            },
        },
        {
            field: SortRepairRequestColumnsEnum.STATE_HISTORY_STATUS,
            headerName: "Статус заявки",
            renderCell: (params) => <StatusCell row={params.row} />,
            disableColumnMenu: true,
            editable: false,
            align: "center",
            headerAlign: "center",
            flex: 1,
        },
        {
            field: 'stateHistory',
            headerName: "История заявки",
            renderCell: (params) => <HistoryCell row={params.row} />,
            disableColumnMenu: true,
            sortable: false,
            editable: false,
            align: 'center',
            headerAlign: 'center',
            flex: 1,
        },
        {
            field: 'actions',
            headerName: "Действия",
            renderCell: (params) => <ActionsCell row={params.row} />,
            disableColumnMenu: true,
            sortable: false,
            editable: false,
            align: 'right',
            headerAlign: 'right',
            flex: 2.5,
        },
    ], []);


    return (
        <Box sx={{height: "100%", width: "100%"}}>
            <DataGrid
                loading={loading}
                rows={equipmentRepairRequest || []}
                columns={columns}
                getRowId={(row) => row.id}
                getRowHeight={() => 50}
                getRowClassName={({ id }) => (id === contextRow?.id && contextMenu !== null) ? "isOpenContextMenu" : ""}
                disableSelectionOnClick={true}
                disableExtendRowFullWidth
                scrollbarSize={8}

                sx={{
                    "& .isOpenContextMenu": {
                        bgcolor: "action.hover"
                    }
                }}

                onRowDoubleClick={(params: GridRowData) => doubleClickHandler(params.row.id)}

                columnBuffer={3}
                columnThreshold={3}
                rowBuffer={5}
                rowThreshold={5}

                rowsPerPageOptions={[10, 50, 100]}
                pageSize={pagination?.pageSize}
                sortingMode='server'
                onSortModelChange={sortModelChangeHandler}
                pagination
                onPageSizeChange={handlePageSizeChange}
                onPageChange={handlePageChange}

                rowCount={allCount || 0}
                page={pagination?.page}
                paginationMode="server"
            />

            <Menu
                open={contextMenu !== null}
                onClose={contextMenuCloseHandler}
                anchorReference='anchorPosition'
                anchorPosition={
                    contextMenu !== null
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                }
                elevation={22}
                sx={{
                    maxWidth: 300,
                    "& .MuiListItemText-root .MuiTypography-root": {
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        width: "100%",
                        display: "flow-root"
                    }
                }}
            >
                {contextRow && <MoreMenuItems
                    equipmentRepairRequest={contextRow}
                    onClose={contextMenuCloseHandler}
                />}
            </Menu>

        </Box>
    );
};

export default List
